import React, { useState, useEffect, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import FundList from '../FundList/FundList';
import FundListFilter from '../FundListFilter/FundListFilter';
import Loader from '../Loader/Loader';
import SelectFundsMethodology from '../SelectFundsMethodology/SelectFundsMethodology';
import { ALL_FUNDS_QUERY } from './queries';
import {
  defaultToggleButtons,
  filterFunds,
  formatFunds,
  groupFunds,
  groupFundsTotals,
  extractActiveFilters,
  getAssetClassNames
} from './util';

import useToggleButtons from '../../hooks/useToggleButtons';
import useToggleButton from '../../hooks/useToggleButton';
import { CartContext } from '../../context/CartContext/CartContext';
import { FlowContext, DEFAULT_LOADING_MESSAGE } from '../../core/flow-engine';
import FundQuickSearch from '../FundQuickSearch/FundQuickSearch';
import FundListFundsInfo from '../FundListFundsInfo/FundListFundsInfo';
import styles from './style.css';

/* eslint-disable complexity */
const FundListFragment = ({
  rewardsAvailable,
  headerOpen,
  filterOpen,
  toggleFilter
}) => {
  const { totalAmount } = useContext(CartContext);
  const { appState } = useContext(FlowContext);
  const {
    store: { accountType, rolloverType },
    chosen
  } = appState;

  const { loading, error, data: { allMutualFunds = [] } = {} } = useQuery(
    ALL_FUNDS_QUERY
  );

  // TODO: create an option to retrieve funds
  // by rollover account type on the server side
  // so we don't have to mutate data in the UI
  // THIS FUNCTION SHOULD CONTAIN ALL BYOP BUSINESS RULES
  // THAT ARE BASED OFF OF STORE DATA
  const MutualFunds = formatFunds(
    allMutualFunds,
    accountType,
    rolloverType,
    totalAmount,
    rewardsAvailable
  );
  const assetClassNames = getAssetClassNames(MutualFunds);

  const [filterText, setFilterText] = useState('');

  const [
    assetClassToggleButtons,
    handleAssetClassToggleClick,
    setAssetClassToggleButtons
  ] = useToggleButtons([]);

  const [
    morningstarButtons,
    handleMorningstarToggleClick,
    setMorningstarToggleButtons
  ] = useToggleButtons([1, 2, 3, 4, 5]);

  const [
    selectFundsButton,
    handleSelectFundsButton,
    setSelectFundsButton
  ] = useToggleButton('Select Funds');

  const [
    investorClassButton,
    handleInvestorClassButton,
    setInvestorClassButton
  ] = useToggleButton('Investor Class');

  const [iClassButton, handleiClassButton, setiClassButton] = useToggleButton(
    'I Class'
  );

  const [
    closedFundsButton,
    handleClosedFundsButton,
    setClosedFundsButton
  ] = useToggleButton('Closed Funds');

  const [
    openToNewInvestorsButton,
    handleOpenToNewInvestorsButton,
    setOpenToNewInvestorsButton
  ] = useToggleButton('Open to New Investors');

  useEffect(() => {
    if (allMutualFunds.length && !assetClassToggleButtons.length) {
      setAssetClassToggleButtons(defaultToggleButtons(assetClassNames));
    }
  }, [assetClassToggleButtons, allMutualFunds]);

  const [searchableFunds, setSearchableFunds] = useState(MutualFunds);
  const [filteredFunds, setFilteredFunds] = useState([]);

  const toggleFilterViewOnOff = () => {
    toggleFilter(!filterOpen);
  };

  useEffect(() => {
    if (headerOpen) toggleFilter(false);
  }, [headerOpen]);

  const clearActiveFilters = () => {
    const filteredAssetClassNames = assetClassNames.filter(
      assetClassName =>
        assetClassName !== 'Multi-Asset' &&
        assetClassName !== 'Equity' &&
        assetClassName !== 'US Equity'
    );
    setAssetClassToggleButtons(
      defaultToggleButtons(filteredAssetClassNames, chosen)
    );
    setMorningstarToggleButtons(defaultToggleButtons([1, 2, 3, 4, 5]));
    setOpenToNewInvestorsButton({
      name: 'Open to New Investors',
      active: false
    });
    setSelectFundsButton({
      name: 'Select Funds',
      active: chosen === 'taxableSelectFunds'
    });
    setInvestorClassButton({
      name: 'Investor Class',
      active: false
    });
    setiClassButton({
      name: 'I Class',
      active: false
    });
    setClosedFundsButton({
      name: 'Closed Funds',
      active: false
    });

    if (filterOpen) toggleFilterViewOnOff();
    setFilterText('');
  };

  useEffect(() => {
    if (allMutualFunds.length) {
      clearActiveFilters();
      setSearchableFunds(MutualFunds);
    }
  }, [totalAmount, allMutualFunds]);

  const groupedFundsTotals = groupFundsTotals(searchableFunds);

  useEffect(() => {
    if (allMutualFunds.length) {
      setFilteredFunds(
        filterFunds(
          formatFunds(
            allMutualFunds,
            accountType,
            rolloverType,
            totalAmount,
            rewardsAvailable
          ),
          filterText,
          extractActiveFilters(assetClassToggleButtons),
          extractActiveFilters(morningstarButtons),
          openToNewInvestorsButton.active,
          selectFundsButton.active,
          iClassButton.active,
          closedFundsButton.active,
          investorClassButton.active
        )
      );
    }
  }, [
    allMutualFunds,
    accountType,
    rolloverType,
    rewardsAvailable,
    filterText,
    assetClassToggleButtons,
    morningstarButtons,
    openToNewInvestorsButton,
    selectFundsButton,
    iClassButton,
    investorClassButton,
    closedFundsButton,
    totalAmount
  ]);

  const [groupedFunds, setGroupedFunds] = useState([]);

  useEffect(() => {
    setGroupedFunds(groupFunds(filteredFunds));
  }, [filteredFunds]);

  const updateFilterText = text => {
    // if there is quick search text selected from AutoSuggest in FundQuickSearch, clear out active filter buttons
    if (text.trim().length > 0) clearActiveFilters();

    setFilterText(text);
  };

  const filterSection = () =>
    filterOpen && (
      <div className="filters-wrapper" data-name="filtersWrapper">
        <FundListFilter
          assetClassToggleButtons={assetClassToggleButtons}
          morningstarButtons={morningstarButtons}
          openToNewInvestorsButton={openToNewInvestorsButton}
          selectFundsButton={selectFundsButton}
          handleAssetClassToggleClick={handleAssetClassToggleClick}
          handleMorningstarToggleClick={handleMorningstarToggleClick}
          handleOpenToNewInvestorsButton={handleOpenToNewInvestorsButton}
          handleSelectFundsButton={handleSelectFundsButton}
          iClassButton={iClassButton}
          investorClassButton={investorClassButton}
          closedFundsButton={closedFundsButton}
          handleiClassButton={handleiClassButton}
          handleInvestorClassButton={handleInvestorClassButton}
          handleClosedFundsButton={handleClosedFundsButton}
          searchableFundsLength={searchableFunds.length}
          filteredFundsLength={filteredFunds.length}
          totalAmount={totalAmount}
          rewardsAvailable={rewardsAvailable}
        />
      </div>
    );

  const numFiltersApplied = extractActiveFilters([
    ...assetClassToggleButtons,
    ...morningstarButtons,
    selectFundsButton,
    investorClassButton,
    iClassButton,
    closedFundsButton,
    openToNewInvestorsButton
  ]).length;

  if (loading) return <Loader message={DEFAULT_LOADING_MESSAGE} isVisible />;
  if (error) return `Error! ${error}`;

  return (
    <>
      <div className="search-filter-view" data-name="filterViewWrapper">
        <div className="filterbtn-fundinfo">
          <div
            className={`expand-filter-view${filterOpen ? ' expanded' : ''}${
              numFiltersApplied ? ' active' : ''
            }`}>
            <button
              data-no-warn
              className={`filter-btn${numFiltersApplied ? ' active' : ''}`}
              type="button"
              onClick={() => toggleFilterViewOnOff()}>
              Filters{' '}
              <span>{numFiltersApplied ? `(${numFiltersApplied})` : ' '}</span>
            </button>
          </div>
          <div className="mobile-only">{filterSection()}</div>

          <FundListFundsInfo
            searchableFundsLength={searchableFunds.length}
            filteredFundsLength={filteredFunds.length}
          />
        </div>
        <FundQuickSearch
          updateFilterText={updateFilterText}
          allMutualFunds={searchableFunds}
          placeholder="Search for Mutual Funds"
          mode="BYOP"
        />
        <SelectFundsMethodology showSelectFundInfo />
      </div>
      <div className="desktop-only">{filterSection()}</div>
      <FundList
        groupedFunds={groupedFunds}
        groupedFundsTotals={groupedFundsTotals}
        filtersApplied={!!numFiltersApplied}
        chosen={chosen}
      />
      <style jsx>{styles}</style>
    </>
  );
};
/* eslint-enable complexity */
export default FundListFragment;
